import React from "react";
import { useNavigate } from "react-router-dom";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { Range } from "react-range";
import { useFetch, useFilter, useLanguage } from "hooks";
import { Layout, CollapseList, Icons, Skeletons } from "components";
import qs from "qs";

export default function AdvanceSearch() {
  const navigate = useNavigate();
  const language = useLanguage(); //language returned from useLanguage hook; it’s an object with current language key --> "value in current language";
  const [clickedTarget, setClickedTarget] = React.useState(null);
  const [filtersResponse, filtersRequest] = useFetch(`getBooksFilters`, "POST");
  const filters = useFilter(filtersResponse.result.data);
  const [searchFieldsResponse, searchFieldsRequest] =
    useFetch(`getBookSearchFields`);
  const [timeFrame, setTimeFrame] = React.useState([1999, 2022]);
  const [fieldList, setFieldList] = React.useState([{ key: "", value: "" }]);
  const [didLoad, setDidLoad] = React.useState(false);

  const handleFieldChange = (e, index) => {
    const { name, value } = e.target;
    const list = [...fieldList];
    list[index][name] = value;
    setFieldList(list);
  };

  const handleFieldRemove = (index) => {
    const list = [...fieldList];
    list.splice(index, 1);
    setFieldList(list);
  };

  const handleFieldAdd = () => {
    setFieldList([...fieldList, { key: "", value: "" }]);
  };

  const onSubmit = () => {
    let querystring = "";
    let newFieldList = [];

    if ([...fieldList].length > 0 && [...fieldList][0].key !== "") {
      // Convert fieldList array into an object of key/val pairs
      newFieldList = [...fieldList].reduce(
        (a, v) => ({ ...a, [v.key]: v.value }),
        {}
      );
    }

    let newFilterList = {};
    filters.selectedFilters.forEach((sf1) => {
      newFilterList[sf1.key] = filters.selectedFilters
        .filter((sf2) => sf1.key === sf2.key)
        .map((sf2) => sf2.values)[0];
    });

    querystring = qs.stringify(
      {
        ...(typeof newFieldList === "object" && { ...newFieldList }),
        date: {
          from: timeFrame[0],
          to: timeFrame[1],
        },
        filters: newFilterList,
      },
      {
        encodeValuesOnly: true,
      }
    );

    navigate(`/search?${querystring}`);
  };

  React.useEffect(() => {
    const paramsFilters = {};
    filters.selectedFilters.forEach((sf1, idx) => {
      paramsFilters[sf1.key] = filters.selectedFilters
        .filter((sf2) => sf1.key === sf2.key)
        .map((sf2) => sf2.values)[0];
    });
    filtersRequest(
      {
        page_type: "advanced_search",
        parent_ids: [],
        filters: paramsFilters,
      },
      null,
      () => {
        setDidLoad(true);
      }
    );
  }, [filtersRequest, filters.selectedFilters]);

  React.useEffect(() => {
    searchFieldsRequest();
  }, [searchFieldsRequest]);

  return (
    <Layout>
      <div id="advanced-search_page" className="page_container pt-5">
        <section className="mb-3">
          <Container>
            <Row className="">
              <Col xs={12} className="mb-4 ">
                <h1 className="text-primary">{language.search}</h1>
              </Col>
            </Row>

            <Row className="mx-xl-5">
              {/* section 1 */}
              <Col xs={12} className="mb-4 ">
                <Container className="bg-white rounded-1 shadow py-4">
                  <Row className="p-4 border-bottom">
                    <Col xs={12}>
                      <h3 className="text-primary">{language.searchAbout}:</h3>
                    </Col>
                  </Row>

                  {searchFieldsResponse.result.data &&
                    fieldList.map((singleService, idx) => (
                      <Row key={idx} className="mt-4">
                        <Col md={6} xs={10} className="mb-2 mb-md-0">
                          <div className="d-flex gap-2">
                            {fieldList.length > 1 && (
                              <img
                                role="button"
                                onClick={() => handleFieldRemove(idx)}
                                src="/images/minus.png"
                                width="40"
                                alt="add"
                              />
                            )}
                            <input
                              id={`input-${idx}`}
                              name="value"
                              type="text"
                              className="form-control rounded-1"
                              value={singleService.value}
                              onChange={(e) =>
                                handleFieldChange(e, idx, singleService.label)
                              }
                              autoComplete="off"
                              required
                            />
                          </div>
                        </Col>
                        <Col md={6} xs={10} className="position-relative">
                          <select
                            id={`select-${idx}`}
                            name="key"
                            type="text"
                            className="form-control rounded-1"
                            value={
                              singleService.key ? singleService.key : "none"
                            }
                            onChange={(e) =>
                              handleFieldChange(e, idx, singleService.label)
                            }
                            required
                          >
                            <option
                              className="bg-white text-black"
                              value="none"
                              disabled
                            >
                              {language.allFields}
                            </option>
                            {searchFieldsResponse.result.data
                              .filter(
                                (item) =>
                                  !fieldList.some(
                                    (item2, idx2) =>
                                      item2.key === item.key && idx !== idx2
                                  ) &&
                                  item.label !== "" &&
                                  item.key !== ""
                              )
                              .map((item, index) => (
                                <option
                                  key={index}
                                  className="bg-white text-black"
                                  value={item.key}
                                >
                                  {item.label}
                                </option>
                              ))}
                          </select>
                          <Icons.ArrowDown2
                            height="20"
                            className="absolute-arrow svg-fill"
                          />
                        </Col>
                        {idx === fieldList.length - 1 &&
                          idx < searchFieldsResponse.result.data.length - 1 && (
                            <Col xs={12} className="mt-2 text-center">
                              <img
                                role="button"
                                onClick={handleFieldAdd}
                                src="/images/plus.png"
                                width="40"
                                alt="add"
                              />
                            </Col>
                          )}
                      </Row>
                    ))}
                </Container>
              </Col>
              {/* section 2 */}
              <Col xs={12} className="mb-4 ">
                <Container className="bg-white rounded-1 shadow py-4">
                  <Row className="p-4 border-bottom">
                    <Col xs={12}>
                      <h3 className="text-primary">{language.date}:</h3>
                    </Col>
                  </Row>

                  <Row className="align-items-center p-4">
                    <Col xs={12}>
                      <div className="px-4">
                        <Range
                          values={timeFrame}
                          direction={`to ${
                            language.direction === "rtl" ? "left" : "right"
                          }`}
                          step={1}
                          min={1900}
                          max={2022}
                          onChange={(range) => {
                            setTimeFrame(range);
                          }}
                          renderTrack={({ props, children }) => (
                            <div
                              onMouseDown={props.onMouseDown}
                              onTouchStart={props.onTouchStart}
                              className="d-flex"
                              style={{
                                ...props.style,
                                width: "100%",
                                height: "133px",
                              }}
                              dir="ltr"
                            >
                              <div
                                ref={props.ref}
                                className="bg-light3 rounded w-100"
                                style={{
                                  height: "5px",
                                  alignSelf: "center",
                                }}
                              >
                                {children}
                              </div>
                            </div>
                          )}
                          renderThumb={({ props }) => (
                            <div
                              {...props}
                              className="d-flex flex-column align-items-center justify-content-center gap-2"
                              style={{
                                ...props.style,
                                height: "133px",
                                width: "53px",
                              }}
                            >
                              <h6 className="py-1 text-primary">
                                {" "}
                                {props.key === 0
                                  ? language.from
                                  : language.to}{" "}
                              </h6>
                              <div
                                className="bg-primary rounded-circle shadow"
                                style={{
                                  height: "28px",
                                  width: "28px",
                                }}
                              ></div>
                              <span className="bg-white rounded-1">
                                <button className="btn btn-outline-primary px-2 py-1">
                                  {timeFrame[props.key]}
                                </button>
                              </span>
                            </div>
                          )}
                        />
                      </div>
                    </Col>
                  </Row>
                </Container>
              </Col>
              {/* skeleton */}
              <Col xs={12}>
                {!didLoad && filtersResponse.loading && (
                  <Skeletons.AdvancedSearchSkeleton />
                )}
              </Col>
              {filtersResponse.result && (
                <>
                  {/* section 3 */}
                  <Col xs={12} className="mb-0 mb-md-5">
                    <Row>
                      {filtersResponse.result.data &&
                        filters.getAllFilters().map((item, index) => {
                          return (
                            item.values.length > 0 && (
                              <Col
                                key={index}
                                md={4}
                                xs={12}
                                className="mb-4  mb-md-0"
                              >
                                <div className="bg-white rounded-1 shadow p-3 ">
                                  <CollapseList
                                    key={index}
                                    className="svg-fill-black"
                                    titleClasses="text-primary"
                                    title={
                                      <>
                                        {item.name}{" "}
                                        {clickedTarget === index &&
                                          filtersResponse.loading && (
                                            <Spinner
                                              animation="border"
                                              size="sm"
                                            />
                                          )}
                                      </>
                                    }
                                  >
                                    <div className="CollapseList-advance-search">
                                      {item.values.map((subItem, subIndex) => (
                                        <Col
                                          key={subIndex}
                                          role="button"
                                          xs={12}
                                          className={`${
                                            filters.selectedFilters.filter(
                                              (f) =>
                                                f.key === item.key &&
                                                f.values.includes(subItem.id)
                                            ).length > 0
                                              ? "chosen"
                                              : ""
                                          } list-item  mb-2`}
                                          onClick={() => {
                                            setClickedTarget(index);
                                            filters.handleToggleFilterValueSingle(
                                              item.key,
                                              subItem.id
                                            );
                                          }}
                                        >
                                          <span className="fw-normal px-2 ">
                                            {subItem.name}
                                          </span>
                                        </Col>
                                      ))}
                                    </div>
                                  </CollapseList>
                                </div>
                              </Col>
                            )
                          );
                        })}
                    </Row>
                  </Col>
                  {/* submit button */}
                  <Col xs={12} className="d-flex justify-content-center">
                    <button
                      onClick={onSubmit}
                      className="btn btn-primary btn-outline-primary-hover px-5 py-3"
                    >
                      {language.searchTheLibrary}
                    </button>
                  </Col>
                </>
              )}
            </Row>
          </Container>
        </section>
      </div>
    </Layout>
  );
}
